/**
 * "Personalizza completamente" — porta un materiale del catalogo dentro la
 * propria organizzazione e nasconde l'originale, in un solo passaggio.
 *
 * Quando serve: l'org vuole cambiare anche NOME / DESCRIZIONE / DATI TECNICI,
 * non solo prezzo e tempo (per quelli basta la personalizzazione, che non
 * duplica nulla e continua a ricevere gli aggiornamenti di listino).
 *
 * Cosa fa:
 *  1. crea una copia del materiale dentro l'org — con lo STESSO codice
 *     dell'originale (possibile da quando il codice è unico per-org);
 *  2. nasconde l'originale di catalogo per quell'org, così in lista e nel
 *     configuratore ne resta uno solo.
 *
 * Limite da conoscere: la copia è un prodotto a sé e NON riceve più gli
 * aggiornamenti del listino fornitore. Per questo va usata solo quando servono
 * modifiche tecniche, non per il semplice prezzo.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';
import { DatabaseMaterial } from '@/hooks/useMaterials';

/** Colonne non inseribili: identità, timestamp, flag di catalogo e campi solo-view. */
const NON_INSERTABLE_KEYS = [
  'id', 'created_at', 'updated_at', 'created_by', 'organization_id',
  'is_system', 'starter_category',
  'net_price', 'family_discount_chain', 'family_discount_pct', 'extra_discount_pct',
  'total_discount_pct', 'has_family_discount', 'has_extra_discount',
  'discount_id', 'discount_org_id', 'discount_valid_until',
  'customization_id', 'custom_net_price', 'custom_installation_time_per_sqm',
  'has_price_override', 'has_time_override', 'catalog_net_price',
  'catalog_installation_time_per_sqm',
  'custom_family_id', 'effective_family_id', 'has_family_override',
] as const;

export const useForkMaterial = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();

  return useMutation({
    mutationFn: async (material: DatabaseMaterial): Promise<{ id: string }> => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione selezionata');
      if (material.organization_id) {
        throw new Error('Questo materiale è già della tua azienda: modificalo direttamente.');
      }

      const clean = { ...(material as Record<string, unknown>) };
      for (const k of NON_INSERTABLE_KEYS) delete clean[k];

      // Il prezzo della copia parte dal netto che l'org paga oggi (listino ×
      // sconto, oppure il prezzo personalizzato se già impostato).
      const startingPrice =
        material.custom_net_price ?? material.net_price ?? material.unit_price ?? 0;

      const { data: created, error } = await supabase
        .from('materials')
        .insert([{
          ...clean,
          organization_id: currentOrganizationId,
          created_by: user?.id ?? null,
          unit_price: startingPrice,
          list_price: material.list_price ?? startingPrice,
          is_active: true,
        } as never])
        .select('id')
        .single();
      if (error) throw error;
      if (!created?.id) throw new Error('Creazione della copia non riuscita');

      // Nasconde l'originale di catalogo per questa org (best effort: se fallisce
      // la copia resta comunque valida, l'utente può nascondere a mano con l'occhio).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: hideErr } = await (supabase as any)
        .from('org_hidden_materials')
        .upsert(
          {
            organization_id: currentOrganizationId,
            material_id: material.id,
            hidden_by: user?.id ?? null,
          },
          { onConflict: 'organization_id,material_id', ignoreDuplicates: true },
        );
      if (hideErr) console.warn('[useForkMaterial] originale non nascosto:', hideErr);

      return { id: created.id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] });
      queryClient.invalidateQueries({ queryKey: ['hidden-materials'] });
      toast.success('Materiale portato nella tua azienda', {
        description: 'Ora è completamente modificabile. L\'originale di catalogo è stato nascosto.',
      });
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'message' in e
        ? String((e as { message: unknown }).message) : String(e);
      toast.error(`Operazione non riuscita: ${msg}`);
    },
  });
};
